import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
} from '@mui/material';
import { Edit, Delete, Reorder, Add } from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';
import {
  GlassCard,
  SectionTitle,
  TableWrapper,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  ActionIconButton,
} from './AdminStyles';

interface SuggestedQuestion {
  id: number;
  text: string;
  category: string;
  order: number;
}

const SuggestedQuestions: React.FC = () => {
  const [questions, setQuestions] = useState<SuggestedQuestion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [newQuestionCategory, setNewQuestionCategory] = useState<string>('');
  const [newQuestionOrder, setNewQuestionOrder] = useState<number>(0);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editingQuestion, setEditingQuestion] = useState<SuggestedQuestion | null>(null);

  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState<boolean>(false);
  const [questionToDeleteId, setQuestionToDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchSuggestedQuestions();
  }, []);

  const fetchSuggestedQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getSuggestedQuestions();
      setQuestions(data);
    } catch (err) {
      setError('Failed to fetch suggested questions.');
      console.error('Error fetching suggested questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setNewQuestionText('');
    setNewQuestionCategory('');
    setNewQuestionOrder(0);
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddQuestion = async () => {
    setLoading(true);
    setError(null);
    try {
      await adminApi.createSuggestedQuestion({
        text: newQuestionText,
        category: newQuestionCategory,
        order: newQuestionOrder,
      });
      alert('Suggested question added successfully!');
      handleCloseAddDialog();
      fetchSuggestedQuestions(); // Refresh the list
    } catch (err) {
      setError('Failed to add suggested question.');
      console.error('Error adding suggested question:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (question: SuggestedQuestion) => {
    setEditingQuestion(question);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingQuestion(null);
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.updateSuggestedQuestion(editingQuestion.id, editingQuestion);
      alert('Suggested question updated successfully!');
      handleCloseEditDialog();
      fetchSuggestedQuestions(); // Refresh the list
    } catch (err) {
      setError('Failed to update suggested question.');
      console.error('Error updating suggested question:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirmDialog = (questionId: number) => {
    setQuestionToDeleteId(questionId);
    setOpenDeleteConfirmDialog(true);
  };

  const handleCloseDeleteConfirmDialog = () => {
    setOpenDeleteConfirmDialog(false);
    setQuestionToDeleteId(null);
  };

  const handleConfirmDeleteQuestion = async () => {
    if (!questionToDeleteId) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteSuggestedQuestion(questionToDeleteId);
      alert('Suggested question deleted successfully!');
      handleCloseDeleteConfirmDialog();
      fetchSuggestedQuestions(); // Refresh the list
    } catch (err) {
      setError('Failed to delete suggested question.');
      console.error('Error deleting suggested question:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReorderQuestion = (questionId: number) => {
    console.log('Reorder question:', questionId);
    // Implement reorder logic
  };

  if (loading && questions.length === 0) {
    return <Typography>Nalaganje predlaganih vprašanj...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <GlassCard elevation={0}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <SectionTitle>Upravljanje Predlaganih Vprašanj</SectionTitle>
          <Button
            variant="contained"
            onClick={handleOpenAddDialog}
            startIcon={<Add />}
            sx={{
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)',
              },
            }}
          >
            Dodaj Vprašanje
          </Button>
        </Box>

        <TableWrapper>
          <StyledTableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="suggested questions table">
              <StyledTableHead>
                <StyledTableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Vprašanje</TableCell>
                  <TableCell>Kategorija</TableCell>
                  <TableCell>Vrstni red</TableCell>
                  <TableCell align="right">Akcije</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {questions.map((question) => (
                  <StyledTableRow key={question.id}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 'bold', color: '#6366f1 !important' }}
                    >
                      #{question.id}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{question.text}</TableCell>
                    <TableCell>{question.category}</TableCell>
                    <TableCell>{question.order}</TableCell>
                    <TableCell align="right">
                      <ActionIconButton
                        onClick={() => handleOpenEditDialog(question)}
                        title="Uredi"
                      >
                        <Edit fontSize="small" />
                      </ActionIconButton>
                      <ActionIconButton
                        onClick={() => handleOpenDeleteConfirmDialog(question.id)}
                        title="Izbriši"
                        style={{ color: '#ef4444', background: '#fee2e2' }}
                      >
                        <Delete fontSize="small" />
                      </ActionIconButton>
                      <ActionIconButton
                        onClick={() => handleReorderQuestion(question.id)}
                        title="Preuredi"
                      >
                        <Reorder fontSize="small" />
                      </ActionIconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </TableWrapper>
      </GlassCard>

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        PaperProps={{ style: { borderRadius: 20, padding: 10 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Dodaj novo predlagano vprašanje</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="text"
            label="Vprašanje"
            type="text"
            fullWidth
            variant="standard"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
          />
          <TextField
            margin="dense"
            id="category"
            label="Kategorija"
            type="text"
            fullWidth
            variant="standard"
            value={newQuestionCategory}
            onChange={(e) => setNewQuestionCategory(e.target.value)}
          />
          <TextField
            margin="dense"
            id="order"
            label="Vrstni red"
            type="number"
            fullWidth
            variant="standard"
            value={newQuestionOrder}
            onChange={(e) => setNewQuestionOrder(parseInt(e.target.value))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} sx={{ borderRadius: '10px', color: '#6b7280' }}>
            Prekliči
          </Button>
          <Button
            onClick={handleAddQuestion}
            sx={{
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              color: 'white',
              '&:hover': { background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)' },
            }}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        PaperProps={{ style: { borderRadius: 20, padding: 10 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Uredi predlagano vprašanje</DialogTitle>
        <DialogContent>
          {editingQuestion && (
            <>
              <TextField
                autoFocus
                margin="dense"
                id="edit-text"
                label="Vprašanje"
                type="text"
                fullWidth
                variant="standard"
                value={editingQuestion.text}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
              />
              <TextField
                margin="dense"
                id="edit-category"
                label="Kategorija"
                type="text"
                fullWidth
                variant="standard"
                value={editingQuestion.category}
                onChange={(e) =>
                  setEditingQuestion({ ...editingQuestion, category: e.target.value })
                }
              />
              <TextField
                margin="dense"
                id="edit-order"
                label="Vrstni red"
                type="number"
                fullWidth
                variant="standard"
                value={editingQuestion.order}
                onChange={(e) =>
                  setEditingQuestion({ ...editingQuestion, order: parseInt(e.target.value) })
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} sx={{ borderRadius: '10px', color: '#6b7280' }}>
            Prekliči
          </Button>
          <Button
            onClick={handleUpdateQuestion}
            sx={{
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              color: 'white',
              '&:hover': { background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)' },
            }}
          >
            Posodobi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteConfirmDialog}
        onClose={handleCloseDeleteConfirmDialog}
        PaperProps={{ style: { borderRadius: 20, padding: 10 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Izbriši predlagano vprašanje</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-question-dialog-description">
            Ali ste prepričani, da želite izbrisati to predlagano vprašanje?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteConfirmDialog}
            sx={{ borderRadius: '10px', color: '#6b7280' }}
          >
            Prekliči
          </Button>
          <Button
            onClick={handleConfirmDeleteQuestion}
            autoFocus
            sx={{
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
            }}
          >
            Izbriši
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuggestedQuestions;
